import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { SidebarProvider, SidebarInset, SidebarTrigger, Sidebar, SidebarHeader, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarFooter, useSidebar } from "@/components/ui/sidebar";
import { MapPin, Building2, Users, Target, Construction, Plug, LogOut, Archive, CheckSquare, Download, Crosshair, Heart, Briefcase, Presentation, Menu, Table as TableIcon } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useProfiles } from "@/hooks/useProfiles";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useSignedStorageUrl } from "@/hooks/useSignedStorageUrl";
import logoImage from "@/assets/greenvision-logo.png";

// Menu structure with section headers
type MenuEntry = 
  | { type: 'item'; icon: typeof MapPin; label: string; href: string }
  | { type: 'label'; label: string };

const menuStructure: MenuEntry[] = [
  { type: 'item', icon: MapPin, label: "Dashboard", href: "/charging" },
  { type: 'label', label: "Persoonlijk" },
  { type: 'item', icon: CheckSquare, label: "To-do's", href: "/charging/todos" },
  { type: 'label', label: "Locaties" },
  { type: 'item', icon: Target, label: "Acquisitie", href: "/charging/acquisitie" },
  { type: 'item', icon: Construction, label: "Realisatie", href: "/charging/realisatie" },
  { type: 'item', icon: Plug, label: "Operatie", href: "/charging/operatie" },
  { type: 'label', label: "Commercieel" },
  { type: 'item', icon: Heart, label: "Wensen", href: "/charging/wensen" },
  { type: 'item', icon: Crosshair, label: "Concurrentie", href: "/charging/concurrentie" },
  { type: 'item', icon: Briefcase, label: "Opportunities", href: "/charging/opportunities" },
  { type: 'item', icon: Presentation, label: "Presentatie", href: "/charging/presentatie" },
  { type: 'label', label: "Algemeen" },
  { type: 'item', icon: Building2, label: "Organisaties", href: "/charging/organisaties" },
  { type: 'item', icon: Users, label: "Contacten", href: "/charging/contacten" },
  { type: 'item', icon: Download, label: "Export", href: "/charging/export" },
  { type: 'item', icon: TableIcon, label: "Tabel", href: "/charging/gauw-tabel" },
  { type: 'item', icon: Archive, label: "Archief", href: "/charging/archief" },
];
const ChargingSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    signOut,
    userRole
  } = useAuth();
  const {
    currentProfile
  } = useProfiles();
  const [showLogoutButton, setShowLogoutButton] = useState(false);
  const {
    state
  } = useSidebar();
  const avatarPath = currentProfile?.avatar_url?.startsWith('avatars/') ? currentProfile.avatar_url : currentProfile?.avatar_url ? `avatars/${currentProfile.avatar_url}` : null;
  const {
    url: avatarUrl
  } = useSignedStorageUrl('pictures', avatarPath);
  const { url: chargingLogoUrl, loading: loadingChargingLogo } = useSignedStorageUrl('pictures', 'GVCC.png');
  
  // Determine logo source with fallback
  const logoSrc = (userRole === 'charging_user' && chargingLogoUrl) ? chargingLogoUrl : logoImage;
  const getDisplayName = () => {
    return currentProfile?.display_name || user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'Gebruiker';
  };
  const getInitials = () => {
    const displayName = getDisplayName();
    if (displayName && displayName !== 'Gebruiker') {
      return displayName.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  return <Sidebar collapsible="offcanvas" className="border-r">
      <SidebarHeader className="p-4 border-b">
        <Link to="/charging" className="flex items-center gap-2">
          <img 
            src={logoSrc} 
            alt="GreenVision Charging Logo" 
            loading="eager" 
            decoding="async"
            onLoad={() => console.log('[ChargingLayout Logo] Loaded:', logoSrc)}
            onError={(e) => {
              console.error('[ChargingLayout Logo] Failed to load:', logoSrc);
              e.currentTarget.src = logoImage;
            }} 
            className="h-8 object-contain" 
          />
        </Link>
      </SidebarHeader>
      
      <SidebarContent className="px-4">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuStructure.map((entry, index) => {
                if (entry.type === 'label') {
                  return (
                    <SidebarGroupLabel key={`label-${index}`} className="mt-4 first:mt-0 text-xs uppercase tracking-wider text-muted-foreground px-2">
                      {entry.label}
                    </SidebarGroupLabel>
                  );
                }
                return (
                  <SidebarMenuItem key={entry.href}>
                    <SidebarMenuButton asChild isActive={location.pathname === entry.href}>
                      <NavLink to={entry.href} className={({ isActive }) => cn("flex items-center gap-3", isActive && "bg-accent text-accent-foreground")}>
                        <entry.icon className="h-4 w-4" />
                        <span>{entry.label}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {user && <SidebarFooter className="p-4 border-t">
          <div className="relative" onMouseEnter={() => setShowLogoutButton(true)} onMouseLeave={() => setShowLogoutButton(false)}>
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10" style={{
            backgroundColor: currentProfile?.avatar_bg_color || '#E5E7EB'
          }}>
                <AvatarImage src={avatarUrl || undefined} />
                <AvatarFallback style={{
              backgroundColor: currentProfile?.avatar_bg_color || '#E5E7EB'
            }} className="text-white font-medium text-sm">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              {state !== "collapsed" && <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {getDisplayName()}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user.email}
                  </p>
                </div>}
            </div>
            
            {showLogoutButton && <div className="absolute top-0 right-0 bg-popover border border-border rounded-lg shadow-lg p-2 z-50">
                <Button variant="ghost" size="sm" onClick={handleLogout} className="flex items-center gap-2 text-destructive hover:text-destructive hover:bg-destructive/10">
                  <LogOut className="h-4 w-4" />
                  {state !== "collapsed" && "Uitloggen"}
                </Button>
              </div>}
          </div>
        </SidebarFooter>}
    </Sidebar>;
};
export function ChargingLayout() {
  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <ChargingSidebar />
        <SidebarInset className="flex flex-col h-full overflow-hidden">
          {/* Mobile Header - alleen zichtbaar op mobiel */}
          <header className="flex h-14 md:hidden shrink-0 items-center gap-2 border-b px-4 bg-background sticky top-0 z-20">
            <SidebarTrigger className="-ml-1">
              <Menu className="h-5 w-5" />
            </SidebarTrigger>
            <Separator orientation="vertical" className="mr-2 h-4" />
            <span className="font-semibold text-sm">GreenVision Charging</span>
          </header>
          <main className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="w-full max-w-full">
              <Outlet />
            </div>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}